import {OdpSegmentCache} from '../utils/odp';

/**
 * Current User bucketing
 * @module
 */
var _ = require('optimizely-client-fn');

var actionTypes = require('../constants/action_types');
var JSONEnv = require('../lib/json');

module.exports = {
  initialize: function() {
    this.__state = {
      // Layer ID to set of Experiment ID --> Variation ID maps for sticky bucketing
      variationIdMap: {},
      // Group ID --> Layer ID map for sticky group bucketing
      preferredLayerMap: {},
      // Contextual MAB data
      contextualMabMap: {},
      // ODP segments
      odpSegments: {},
    };

    this.on(actionTypes.UPDATE_VARIATION_ID_MAP, this.__updateVariationIdMap);
    this.on(actionTypes.MERGE_VARIATION_ID_MAP, this.__mergeVariationIdMap);

    this.on(actionTypes.UPDATE_PREFERRED_LAYER_MAP, this.__updatePreferredLayerMap);
    this.on(actionTypes.MERGE_PREFERRED_LAYER_MAP, this.__mergePreferredLayerMap);

    this.on(actionTypes.UPDATE_CONTEXTUAL_MAB_MAP, this.__updateContextualMabMap);
    this.on(actionTypes.MERGE_CONTEXTUAL_MAB_MAP, this.__mergeContextualMabMap);

    this.on(actionTypes.SET_ODP_SEGMENTS, this.__setOdpSegments);
  },
  /**
   * Set ODP segments
   * @param {OdpSegmentCache} payload
   */
  __setOdpSegments: function(payload: OdpSegmentCache) {
    this.__state.odpSegments = payload;
    this.emitChange();
  },

  /**
   * Get ODP segments
   * @returns {OdpSegmentCache}
   */
  getOdpSegments: function(): OdpSegmentCache {
    return this.__state.odpSegments;
  },

  /**
   * Return contextualMabMap - mapping of layerId -> experimentId -> variationData
   * null if visitor has never been bucketed into any experiences
   * @returns {Object.<Object.<Object>>|null}
   */
  getContextualMabMap: function(): Record<string, Record<string, { variationId: string }>>|null {
    return _.cloneDeep(this.__state.contextualMabMap);
  },

  /**
   * @returns {string} JSONEnv.stringify'd `ContextualMabMap`
   */
  getContextualMabMapString: function(): string {
    return JSONEnv.stringify(this.__state.contextualMabMap);
  },

  /**
   * Updates layerId -> experimentId -> variationData mapping
   * @private
   * @param {string} payload.layerId
   * @param {string} payload.experimentId
   * @param {Object} payload.variationData
   */
  __updateContextualMabMap: function(payload) {
    var contextualMabMap = this.__state.contextualMabMap;
    var currentLayerMap = contextualMabMap[payload.layerId] || {};
    if (!_.isEqual(currentLayerMap[payload.experimentId], payload.variationData)) {
      currentLayerMap[payload.experimentId] = payload.variationData;
      this.__state.contextualMabMap[payload.layerId] = currentLayerMap;
      this.emitChange();
    }
  },

  /**
   * Merges the contextualMabMap without overwriting existing data
   * @private
   * @param {Object} payload.contextualMabMap
   */
  __mergeContextualMabMap: function(payload) {
    var contextualMabMap = this.__state.contextualMabMap;
    _.forEach(payload.contextualMabMap, function(experimentMap, layerId) {
      if (!contextualMabMap[layerId]) {
        contextualMabMap[layerId] = {};
      }
      _.forEach(experimentMap, function(variationData, experimentId) {
        if (!contextualMabMap[layerId][experimentId]) {
          contextualMabMap[layerId][experimentId] = variationData;
        }
      });
    });
    this.__state.contextualMabMap = contextualMabMap;
    this.emitChange();
  },

  /**
   * Return variationIdMap - mapping of layerId -> experimentId -> variationId
   * null if visitor has never been bucketed into any experiences
   * @returns {Object.<Object.<string>>|null}
   */
  getVariationIdMap: function(): Record<string, Record<string, string>>|null {
    return _.cloneDeep(this.__state.variationIdMap);
  },

  /**
   * @returns {string} JSONEnv.stringify'd `PreferredVariationMap`
   */
  getVariationIdMapString: function(): string {
    return JSONEnv.stringify(this.__state.variationIdMap);
  },

  /**
   * Updates layerId -> experimentId -> variationId mapping
   * @private
   * @param {string} payload.layerId
   * @param {string} payload.experimentId
   * @param {string} payload.variationId
   */
  __updateVariationIdMap: function(payload) {
    var variationIdMap = this.__state.variationIdMap;
    var currentLayerMap = variationIdMap[payload.layerId] || {};
    if (currentLayerMap[payload.experimentId] !== payload.variationId) {
      currentLayerMap[payload.experimentId] = payload.variationId;
      this.__state.variationIdMap[payload.layerId] = currentLayerMap;
      this.emitChange();
    }
  },

  /**
   * Updates variationIdMap, but doesn't overwrite any data
   * @param {Object} payload.variationIdMap
   */
  __mergeVariationIdMap: function(payload) {
    var currentVariationIdMap = this.getVariationIdMap();
    var foreignVariationIdMap = payload.variationIdMap;
    // Merge experiment -> variation map for each layer
    // This is a two-level-deep merge of currentVariationIdMap into foreignVariationIdMap
    _.each(currentVariationIdMap || {}, function(layerMap, layerId) {
      if (!foreignVariationIdMap[layerId]) {
        foreignVariationIdMap[layerId] = layerMap;
      } else {
        _.assign(foreignVariationIdMap[layerId], layerMap);
      }
    });
    this.__state.variationIdMap = foreignVariationIdMap;
    this.emitChange();
  },

  /**
   * Get the groupId-->layerId map for the visitor
   * @returns {Object.<string>|null}
   */
  getPreferredLayerMap: function(): Record<string, string>|null {
    return _.cloneDeep(this.__state.preferredLayerMap);
  },

  /**
   * @returns {string} JSONEnv.stringify'd Object
   */
  getPreferredLayerMapString: function(): string {
    return JSONEnv.stringify(this.__state.preferredLayerMap);
  },

  /**
   * Get the preferred layer id for the given group id, if it exists
   * undefined if the visitor has not been bucketed into this group before
   * @param {string} groupId
   * @returns {string|undefined}
   */
  getPreferredLayerId: function(groupId:string): string|undefined {
    return this.__state.preferredLayerMap[groupId];
  },

  /**
   * Updates groupId -> layerId mapping
   * @private
   * @param {string} payload.groupId
   * @param {string} payload.layerId
   */
  __updatePreferredLayerMap: function(payload) {
    if (this.__state.preferredLayerMap[payload.groupId] !== payload.layerId) {
      this.__state.preferredLayerMap[payload.groupId] = payload.layerId;
      this.emitChange();
    }
  },

  /**
   * Updates preferredLayerMap, but doesn't overwrite any data
   * @private
   * @param {Object.<string>} payload.preferredLayerMap
   */
  __mergePreferredLayerMap: function(payload) {
    var currentPreferredLayerMap = this.getPreferredLayerMap();
    var foreignPreferredLayerMap = payload.preferredLayerMap;

    _.assign(foreignPreferredLayerMap, currentPreferredLayerMap);

    this.__state.preferredLayerMap = foreignPreferredLayerMap;
    this.emitChange();
  },
};
